import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  ArrowLeft,
  Calendar,
  Clock,
  Send,
  AlertCircle,
  CheckCircle,
  XCircle,
  User,
  FileText
} from 'lucide-react';
import { toast } from 'react-toastify';

interface ExtensionRequest {
  _id: string;
  task: {
    _id: string;
    title: string;
    description: string;
    currentDeadline: string;
    project: {
      _id: string;
      title: string;
      client: {
        _id: string;
        firstName: string;
        lastName: string;
      };
    };
  };
  requestedBy: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  requestedDeadline: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  response?: string;
  respondedBy?: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  respondedAt?: string;
  createdAt: string;
}

const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    // Special handling for WebContainer preview URLs
    if (hostname.includes('webcontainer.io') || hostname.includes('webcontainer-api.io')) {
      const baseUrl = hostname.replace('--5173--', '--5002--');
      return `${protocol}//${baseUrl}/api`;
    }

    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:5002/api`;
    }

    // Render or other deployment domains
    return `${protocol}//${hostname}/api`;
  }

  // Fallback to env or default
  return import.meta.env.VITE_API_URL || 'http://localhost:5002/api';
};

const DeadlineExtensionRequest: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [request, setRequest] = useState<ExtensionRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [response, setResponse] = useState<string>('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // New request form
  const [showNewRequestForm, setShowNewRequestForm] = useState(false);
  const [newRequest, setNewRequest] = useState({
    taskId: '',
    requestedDeadline: '',
    reason: ''
  });
  const [tasks, setTasks] = useState<any[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);

  const API_BASE_URL = getApiBaseUrl();

  useEffect(() => {
    if (id) {
      fetchExtensionRequest();
    } else {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (showNewRequestForm) {
      fetchAssignedTasks();
    }
  }, [showNewRequestForm]);

  const fetchExtensionRequest = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/deadline-extensions/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch extension request');
      }

      const data = await response.json();
      setRequest(data.extension);
    } catch (error) {
      setError('Failed to load extension request');
      toast.error('Failed to load extension request');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignedTasks = async () => {
    try {
      setLoadingTasks(true);
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/tasks/assigned/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch tasks');
      }

      const data = await response.json();
      setTasks(data.tasks || []);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoadingTasks(false);
    }
  };

  const handleResponse = async (approved: boolean) => {
    if (!request) return;
    
    try {
      setSubmitting(true);
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_BASE_URL}/deadline-extensions/${request._id}/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          approved,
          response
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Failed to respond to extension request');
      }

      await fetchExtensionRequest();
      setResponse('');
      toast.success(`Extension request ${approved ? 'approved' : 'rejected'} successfully`);
    } catch (err: any) {
      setError(err.message || 'Failed to respond to extension request');
      toast.error(err.message || 'Failed to respond to extension request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newRequest.taskId || !newRequest.requestedDeadline || !newRequest.reason) {
      setError('All fields are required');
      return;
    }
    
    try {
      setSubmitting(true);
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/deadline-extensions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newRequest)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to create extension request');
      }

      const data = await response.json();
      toast.success('Extension request created successfully');
      
      // Navigate to the new request
      navigate(`/dashboard/extension-requests/${data.extension._id}`);
    } catch (err: any) {
      setError(err.message || 'Failed to create extension request');
      toast.error(err.message || 'Failed to create extension request');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'approved': return 'text-green-600 bg-green-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock className="w-5 h-5" />;
      case 'approved': return <CheckCircle className="w-5 h-5" />;
      case 'rejected': return <XCircle className="w-5 h-5" />;
      default: return <Clock className="w-5 h-5" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getRelativeTime = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now.getTime() - time.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const getDaysExtension = (currentDeadline: string, requestedDeadline: string) => {
    const current = new Date(currentDeadline);
    const requested = new Date(requestedDeadline);
    const diffMs = requested.getTime() - current.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  if (!request && !showNewRequestForm) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Extension request not found</h3>
        <p className="text-gray-600 mb-6">The deadline extension request you're looking for doesn't exist.</p>
        <div className="space-x-4">
          <Link
            to="/dashboard/tasks"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tasks
          </Link>
          <button
            onClick={() => setShowNewRequestForm(true)}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
          >
            <Send className="w-4 h-4 mr-2" />
            New Request
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/dashboard/tasks"
            className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors duration-200"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Deadline Extension Request</h1>
            <p className="text-gray-600 mt-1">Review and manage deadline extension</p>
          </div>
        </div>
        {!showNewRequestForm && !request && (
          <button
            onClick={() => setShowNewRequestForm(true)}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
          >
            <Send className="w-4 h-4 mr-2" />
            New Request
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700 text-sm">{error}</span>
          </div>
        </div>
      )}

      {/* New Request Form */}
      {showNewRequestForm && (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Request Deadline Extension</h2>
          </div>
          
          <form onSubmit={handleNewRequest} className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Task *
              </label>
              {loadingTasks ? (
                <div className="animate-pulse h-10 bg-gray-200 rounded"></div>
              ) : (
                <select
                  value={newRequest.taskId}
                  onChange={(e) => setNewRequest(prev => ({ ...prev, taskId: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select a task</option>
                  {tasks.map(task => (
                    <option key={task._id} value={task._id}>
                      {task.title} - Due: {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : 'No deadline'}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Requested New Deadline *
              </label>
              <input
                type="date"
                value={newRequest.requestedDeadline}
                onChange={(e) => setNewRequest(prev => ({ ...prev, requestedDeadline: e.target.value }))}
                required
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Extension *
              </label>
              <textarea
                value={newRequest.reason}
                onChange={(e) => setNewRequest(prev => ({ ...prev, reason: e.target.value }))}
                required
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Please explain why you need additional time..."
              />
            </div>

            <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowNewRequestForm(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors duration-200"
              >
                {submitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Extension Request Details */}
      {request && (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Calendar className="w-8 h-8 text-blue-600" />
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{request.task.title}</h2>
                  <p className="text-gray-600">Project: {request.task.project.title}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span
                  className={`flex items-center px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(request.status)}`}
                >
                  {getStatusIcon(request.status)}
                  <span className="ml-2 capitalize">{request.status}</span>
                </span>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Request Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Extension Request Details</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Current Deadline
                    </label>
                    <p className="text-gray-900">{formatDate(request.task.currentDeadline)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Requested Deadline
                    </label>
                    <p className="text-gray-900">{formatDate(request.requestedDeadline)}</p>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-3">
                  <div className="flex items-center space-x-2">
                    <Clock className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-900">
                      Extension: {getDaysExtension(request.task.currentDeadline, request.requestedDeadline)} days
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Requester Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Request Information</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">
                      {request.requestedBy.firstName[0]}{request.requestedBy.lastName[0]}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      Requested by {request.requestedBy.firstName} {request.requestedBy.lastName}
                    </p>
                    <p className="text-sm text-gray-600">
                      {getRelativeTime(request.createdAt)}
                    </p>
                  </div>
                </div>

                <div className="bg-yellow-50 rounded-lg p-4">
                  <h4 className="font-medium text-yellow-900 mb-2">Reason for Extension</h4>
                  <p className="text-yellow-800">{request.reason}</p>
                </div>
              </div>
            </div>

            {/* Client Response Section */}
            {request.status === 'pending' && user?.role === 'client' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Response</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Response Message (Optional)
                    </label>
                    <textarea
                      value={response}
                      onChange={(e) => setResponse(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Add any comments about this extension request..."
                    />
                  </div>

                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => handleResponse(true)}
                      disabled={submitting}
                      className="flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors duration-200"
                    >
                      <CheckCircle className="w-5 h-5 mr-2" />
                      {submitting ? 'Processing...' : 'Approve Extension'}
                    </button>
                    <button
                      onClick={() => handleResponse(false)}
                      disabled={submitting}
                      className="flex items-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors duration-200"
                    >
                      <XCircle className="w-5 h-5 mr-2" />
                      {submitting ? 'Processing...' : 'Reject Extension'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Response Display */}
            {request.status !== 'pending' && request.response && request.respondedBy && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Client Response</h3>
                <div className={`rounded-lg p-4 ${
                  request.status === 'approved' ? 'bg-green-50' : 'bg-red-50'
                }`}>
                  <div className="flex items-center space-x-2 mb-2">
                    {request.status === 'approved' ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                    <span className={`font-medium ${
                      request.status === 'approved' ? 'text-green-900' : 'text-red-900'
                    }`}>
                      Extension {request.status} by {request.respondedBy.firstName} {request.respondedBy.lastName}
                    </span>
                    {request.respondedAt && (
                      <span className="text-sm text-gray-500">
                        • {getRelativeTime(request.respondedAt)}
                      </span>
                    )}
                  </div>
                  <p className={request.status === 'approved' ? 'text-green-800' : 'text-red-800'}>
                    {request.response}
                  </p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-between pt-6 border-t border-gray-200">
              <Link
                to={`/dashboard/projects/${request.task.project._id}`}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                View Project Details
              </Link>
              
              <Link
                to={`/dashboard/tasks/${request.task._id}`}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
              >
                <FileText className="w-4 h-4 mr-2" />
                View Task
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeadlineExtensionRequest;